import { PixelData } from "@/core/utils/image-data"
import { getMaskedPixels, getMean } from "@/lib/ai/calibration/ai-calibration-utils"
import { FaceDetectionResult } from "@/lib/ai/detection/face-detection"
import { clamp } from "@/utils/math"
import { Adjustments } from "@/core/types"
import { logAiInfo, logAutoLight } from "@/lib/ai/utils"
import { isFaceDarkSkin, isFaceProblematic } from "../color-match/face-tone"
import { vec4 } from "gl-matrix"

interface FaceLuminanceStatistics {
	facePercentiles: number[]
	facePercent: number
	faceLum: number
	faceCount: number
	faceClippingHighlights?: number
	faceClippingShadows?: number
}

export interface LuminanceStatistics extends FaceLuminanceStatistics {
	avgLum: number
	shadowsMean: number
	clippingPercent: number
	shadowsClippingPercent?: number
	percentiles: number[]
}

export interface AutoExposureParams {
	exposureDelta: number
	highlightsDelta: number
	shadowsDelta: number
	whitesDelta: number
	blacksDelta: number
	contrastDelta: number
	saturationDelta: number
}

export interface LuminanceScoreStatistics {
	avgLum: number
	shadowsMean: number
	clippingPercent: number
	shadowsClippingPercent?: number
	lowerHalfHighlightsClippingPercent?: number
	percentiles: number[]
	lightingScore: number
	autoLightParams?: Partial<Adjustments>
	adjustedGamma?: number
}

export function getLuminanceStatistics(
	imageDataSmall: PixelData | ImageData, // should be downsampled (max length = 300)
	imageDataLarge: PixelData | ImageData, // should be downsampled (max length = 2000)
	faceDetectionResults: FaceDetectionResult[],
): LuminanceStatistics {
	const smallData = imageDataSmall.data
	const lum = getLightness(smallData)
	const histogram = computeHistogram(lum)
	const avgLum = getMean(lum)

	const shadowsMean = getShadowsMean(histogram)

	// Calculate highlights clipping
	let sum = 0
	for (let i = 251; i <= 255; i++) {
		sum += histogram[i] ?? 0
	}
	const clippingPercent = sum / lum.length

	// Calculate shadows clipping
	const shadowsSum = histogram[0] ?? 0
	const shadowsClippingPercent = shadowsSum / lum.length

	const percentiles = computePercentiles(histogram)

	const faceAreaResult = getFaceAreaLuminanceStatistics(imageDataLarge, faceDetectionResults)

	return {
		avgLum,
		shadowsMean,
		clippingPercent,
		shadowsClippingPercent,
		percentiles,
		...faceAreaResult,
	}
}

export function getFaceAreaLuminanceStatistics(
	imageDataLarge: PixelData | ImageData, // should be downsampled (max length = 2000)
	faceDetectionResults: FaceDetectionResult[],
	facePercentAndPixels?: { maskedPercent: number; maskedPixels: number[] },
): FaceLuminanceStatistics {
	let faceLum = 0
	let facePercent = 0
	let facePercentiles: number[] = []
	let faceClippingHighlights = 0
	let faceClippingShadows = 0

	const faceCount = faceDetectionResults.length
	if (faceDetectionResults.length) {
		facePercentAndPixels =
			facePercentAndPixels ?? getMaskedPixels(imageDataLarge, faceDetectionResults, true)
		const facePixels = facePercentAndPixels.maskedPixels
		facePercent = facePercentAndPixels.maskedPercent

		const faceLumArray = getLightness(facePixels)
		const faceLumHistogram = computeHistogram(faceLumArray)

		faceLum = getMean(faceLumArray)
		facePercentiles = computePercentiles(faceLumHistogram)

		// Calculate face highlights clipping
		let highlightsSum = 0
		for (let i = 251; i <= 255; i++) {
			highlightsSum += faceLumHistogram[i] ?? 0
		}
		faceClippingHighlights = highlightsSum / faceLumArray.length

		// Calculate face shadows clipping
		const shadowsSum = faceLumHistogram[0] ?? 0
		faceClippingShadows = shadowsSum / faceLumArray.length
	}

	return {
		facePercentiles,
		facePercent,
		faceLum,
		faceCount,
		faceClippingHighlights,
		faceClippingShadows,
	}
}

export function getMaxExposureWithFace(light: LuminanceStatistics): number {
	let maxExposure = getMaxExposure(light.percentiles)
	if (light.faceLum > 0.01) {
		const minExposure = Math.min(0.55 - light.faceLum, maxExposure, 0.0) * 3
		maxExposure = Math.max(maxExposure, minExposure)
	}
	return maxExposure
}

export function getMaxExposure(percentiles: number[]): number {
	const scale = 3
	const percentile99 = percentiles[percentiles.length - 1] ?? 0
	const percentile95 = percentiles[percentiles.length - 2] ?? 0
	const percentile90 = percentiles[percentiles.length - 3] ?? 0
	const percentile50 = percentiles[2] ?? 0

	const maxExposure99 = (1.04 - percentile99) * scale
	const maxExposure95 = (0.98 - percentile95) * scale
	const maxExposure90 = (0.92 - percentile90) * scale
	const maxExposure50 = (0.42 - percentile50) * scale
	const maxExposure = Math.max(
		maxExposure50,
		Math.min(maxExposure90, maxExposure95, maxExposure99),
	)
	return maxExposure
}

/**
 * Main rule-based auto-lighting algorithm. Computes deltas for exposure,
 * highlights, shadows, whites, blacks, contrast, and saturation from
 * luminance statistics (percentiles, means, face data).
 *
 * ### High-level flow
 *
 * 1. **Face exposure**: compare face percentiles against learned population
 *    means/stds → percentile-difference-based delta, capped to avoid blowing
 *    out face highlights. Weight ≈ 0.8 (reduced for tiny faces).
 *
 * 2. **Image exposure**: same percentile-difference approach on the whole image.
 *    For non-face photos, blends with a shadows-focused estimate to be gentler.
 *
 * 3. **Combine**: weighted sum of face and image exposure. Multiple safety
 *    clamps prevent over-brightening (clipping-aware max exposure, face-limited
 *    range, non-face dampening for negative exposure).
 *
 * 4. **Highlights / whites**: recover clipped highlights from top percentiles.
 *    Also detects "compressed" percentile gaps (blown-out skies). Face-aware
 *    scaling avoids darkening bright skin.
 *
 * 5. **Shadows / blacks**: lift crushed shadows based on shadowsMean and face
 *    low percentiles. If shadows are large, some exposure budget is traded in.
 *
 * 6. **Cross-parameter balancing**: exposure and highlights affect each other —
 *    if exposure exceeds the absolute max, the excess becomes extra highlight
 *    recovery. Bright outdoor clipped photos get an exposure boost to compensate
 *    for heavy highlight reduction.
 *
 * 7. **Saturation compensation**: large shadows + highlight recovery can
 *    increase perceived saturation — a negative vibrance delta is applied.
 *
 * `relaxLimits` widens clamping ranges (used for color match base exposure
 * where the neural LUT provides additional correction, or whenever second iteration
 * of auto light is not computed).
 */
export function getAutoExposure(
	inputs: LuminanceStatistics,
	relaxLimits: boolean = false,
): AutoExposureParams {
	const {
		avgLum,
		faceLum,
		percentiles,
		facePercentiles,
		facePercent,
		shadowsMean,
		clippingPercent,
	} = inputs
	logAutoLight("Inputs:", inputs)

	const scale = 3

	// prettier-ignore
	let means = [
		0.131, 0.26, 0.495, 0.699, 0.846, 0.905, 0.964, 0.503, // whole
		0.399, 0.543, 0.665, 0.74, 0.806, 0.842, 0.893, 0.648 // face
	]
	// prettier-ignore
	let stds = [
		0.09, 0.128, 0.156, 0.131, 0.087, 0.063, 0.035, 0.112,
		0.101, 0.093, 0.079, 0.068, 0.064, 0.064, 0.065, 0.068
	]
	const nofaceMeans = [0.196, 0.331, 0.518, 0.707, 0.85, 0.905, 0.953, 0.536]
	const nofaceStds = [0.159, 0.206, 0.201, 0.155, 0.093, 0.066, 0.039, 0.154]

	const faceExists = facePercentiles.length > 0
	const percentile99 = percentiles[percentiles.length - 1] ?? 0
	const percentile95 = percentiles[percentiles.length - 2] ?? 0
	const percentile90 = percentiles[percentiles.length - 3] ?? 0
	const percentile75 = percentiles[3] ?? 0
	const percentile50 = percentiles[2] ?? 0
	const facePercentile99 = facePercentiles[facePercentiles.length - 1] ?? 0
	const facePercentile95 = facePercentiles[facePercentiles.length - 2] ?? 0
	const facePercentile90 = facePercentiles[facePercentiles.length - 3] ?? 0

	// Face
	let faceWeight = 0
	let faceExposure = 0
	let maxFaceExposure = 0
	let imageExposure = 0

	if (faceExists) {
		faceWeight = 0.8

		if (facePercent < 0.01) {
			faceWeight = 0.6 + (0.2 * facePercent) / 0.01
		}

		const maxDiff = getPercentileDifference(
			facePercentiles.concat(faceLum),
			means.slice(8),
			stds.slice(8),
			0.02,
			[-1],
		)

		const maxDiff2 = getPercentileDifference(
			facePercentiles.concat(faceLum),
			means.slice(8),
			stds.slice(8),
			0.02,
			[5, 6],
		)

		const minMaxDiff = Math.min(maxDiff2, maxDiff)
		faceExposure = -minMaxDiff * scale

		maxFaceExposure = Math.max(0.95 - facePercentile95, 0.9 - facePercentile90) * scale
		faceExposure = Math.min(faceExposure, maxFaceExposure)

		logAutoLight(
			"face exposure",
			faceExposure,
			"face weight",
			faceWeight,
			"max_face_exposure",
			maxFaceExposure,
		)
	}

	// Whole image
	const imageWeight = 1 - faceWeight

	if (!faceExists) {
		means = nofaceMeans
		stds = nofaceStds
	}

	const maxDiff = getPercentileDifference(
		percentiles.concat(avgLum),
		means.slice(0, 8),
		stds.slice(0, 8),
	)

	let imageShadows = 0
	const maxDiffLow = getPercentileDifference(
		percentiles.concat(avgLum),
		means.slice(0, 8),
		stds.slice(0, 8),
		0.15,
		[4, 5, 6],
	)
	const maxDiffDark = getPercentileDifference(
		percentiles.concat(avgLum),
		means.slice(0, 8),
		stds.slice(0, 8),
		0.15,
		[2, 3, 4, 5, 6],
	)
	logAutoLight("-maxDiff:", -maxDiff, "-maxDiffLow:", -maxDiffLow)

	//maxDiffLow = Math.min(maxDiff, maxDiffLow)
	imageShadows = -maxDiffLow * scale
	imageExposure = -maxDiff * scale
	const imageBlacks = -maxDiffDark * scale
	logAutoLight(
		"imageShadows:",
		imageShadows,
		"imageExposure:",
		imageExposure,
		"imageBlacks:",
		imageBlacks,
	)

	if (imageShadows < -0.2) {
		imageShadows = Math.max(imageShadows, imageBlacks)
	}

	let maxExposure = (1.02 - percentile99) * scale
	const maxExposure95 = (1.02 - 0.06 - percentile95) * scale
	const maxExposure90 = (1.02 - 0.12 - percentile90) * scale
	const maxExposure50 = (0.32 - percentile50) * scale
	maxExposure = Math.min(maxExposure, maxExposure95, maxExposure90)
	maxExposure = Math.max(maxExposure, maxExposure50)

	// for non-face photo, try to not decrease its exposure unless necessary
	if (!faceExists) {
		// combine shadows part value with normal value
		const shadowsScale = 0.4
		imageExposure = imageExposure * (1 - shadowsScale) + imageShadows * shadowsScale

		const d1 = (0.98 - percentile99) * scale
		const d2 = (0.93 - percentile95) * scale
		maxExposure = d1 < 0.1 && d2 > d1 ? 0.75 * d1 + 0.25 * d2 : d1
		maxExposure = Math.max(maxExposure, maxExposure50)
		if (clippingPercent > 0) {
			const maxStrict = Math.max(0, (0.95 - percentile99) * scale)
			const maxStrict2 = Math.max(0, (0.55 - percentile75) * scale)
			const maxStrict3 = Math.max(0, (0.32 - percentile50) * scale)
			maxExposure = Math.min(maxExposure, Math.max(maxStrict, maxStrict2, maxStrict3))
		}
	}

	let maxExposureFinal = (Math.max(imageExposure, -0.2) + maxExposure) * 0.6 + 0.3

	logAutoLight({ imageExposure, imageWeight, maxExposure, maxExposureFinal })

	imageExposure = Math.min(imageExposure, maxExposure)

	// Limit range of image exposure by face
	let imageExposureLimited = imageExposure

	if (faceExists) {
		let min_v = faceExposure - 0.4
		let max_v = faceExposure + 0.4

		if (facePercent > 0.007) {
			if (faceExposure < 0) {
				min_v = Math.max(faceExposure - 0.4, Math.min(faceExposure * 5, -0.25))
			} else {
				max_v = Math.min(faceExposure + 0.4, Math.max(0.25, faceExposure * 5))
			}
		}
		const smallFaceThresh = 0.003
		const tinyFaceThresh = 0.001
		if (facePercent < smallFaceThresh) {
			const smallFaceScale = (smallFaceThresh - facePercent) / smallFaceThresh
			min_v -= 0.4 * smallFaceScale
			max_v += 0.4 * smallFaceScale
			maxExposureFinal -= 0.4 * smallFaceScale
			if (clippingPercent > 0)
				maxExposureFinal *= Math.min(1, (1 - smallFaceScale) * 0.5 + 0.5)
			if (facePercent < tinyFaceThresh) {
				const scale = (facePercent / tinyFaceThresh) * 0.6 + 0.4
				logAutoLight("tiny face scale:", scale)
				faceExposure *= scale
				maxExposureFinal *= scale
			}
		}

		imageExposureLimited = Math.min(Math.max(imageExposure, min_v), max_v)
	}

	// Combine face and whole image exposure
	let exposure = imageExposureLimited * imageWeight + faceExposure * faceWeight

	if (!faceExists) {
		// don't decrease exposure too much for nonclipped non-face photos
		if (percentile99 < 0.97 && clippingPercent === 0 && exposure < -0.1) {
			logAutoLight("non-face exposure before lowered:", exposure)
			exposure = -0.1 + (exposure + 0.1) * 0.5
		}
	}

	if (clippingPercent > 0) {
		maxExposureFinal *= 0.7
	}
	if (relaxLimits && maxExposureFinal < faceExposure) {
		maxExposureFinal = maxExposureFinal * 0.2 + faceExposure * 0.8
	}
	logAutoLight("combined exposure:", {
		imageExposureLimited,
		faceExposure,
		exposure,
		maxExposureFinal,
	})

	exposure = Math.min(exposure, maxExposureFinal)

	// Compute highlights
	let highlights =
		Math.min(0.97 - percentile99, 0) +
		Math.min(0.93 - percentile95, 0) +
		Math.min(0.86 - percentile90, 0)

	const highlightsForLowPercentiles =
		Math.min((means[2] ?? 0 + (stds[2] ?? 0)) - (percentiles[2] ?? 0), 0) +
		Math.min((means[3] ?? 0 + (stds[3] ?? 0)) - (percentiles[3] ?? 0), 0)

	let delta1 = 0.07
	let delta2 = 0.07
	if (percentile95 > 0.82) {
		delta1 = percentile99 - percentile95
	}
	if (percentile90 > 0.78) {
		delta2 = percentile95 - percentile90
	}
	const highlightsForClosePercentiles =
		Math.max(Math.min(delta1 - 0.07, 0) + Math.min(delta2 - 0.07, 0), -0.1) * 1.4

	logAutoLight("initial hightlights (from image percentiles):", highlights, {
		highlightsForLowPercentiles,
		highlightsForClosePercentiles,
	})
	highlights = Math.min(highlights, highlightsForLowPercentiles, highlightsForClosePercentiles)

	if (faceExists) {
		highlights = Math.max(highlights, -0.12) * (0.2 + 2 * imageWeight)

		const faceHighlights =
			Math.min(0.8 - facePercentile90, 0) +
			Math.min(0.84 - facePercentile95, 0) +
			Math.min(0.89 - facePercentile99, 0)
		const offset = 8
		const faceHighlightsForLowPercentiles =
			Math.min(
				(means[offset + 2] ?? 0 + (stds[offset + 2] ?? 0)) - (facePercentiles[2] ?? 0),
				0,
			) +
			Math.min(
				(means[offset + 3] ?? 0 + (stds[offset + 3] ?? 0)) - (facePercentiles[3] ?? 0),
				0,
			)

		let delta1 = 0.07
		let delta2 = 0.07
		if (facePercentile95 > 0.82) {
			delta1 = facePercentile99 - facePercentile95
		}
		if (facePercentile90 > 0.78) {
			delta2 = facePercentile95 - facePercentile90
		}
		const faceHighlightsForClosePercentiles =
			Math.max(Math.min(delta1 - 0.05, 0) + Math.min(delta2 - 0.05, 0), -0.08) * 1.5

		logAutoLight("initial hightlights (from face percentiles):", {
			highlights,
			faceHighlights,
			faceHighlightsForLowPercentiles,
			faceHighlightsForClosePercentiles,
		})
		highlights += Math.min(
			faceHighlights,
			faceHighlightsForLowPercentiles,
			faceHighlightsForClosePercentiles,
		)
	}

	const maxHighlights = clippingPercent > 0 ? -25 : -35
	highlights = Math.max(highlights * 200, maxHighlights)
	let whites = highlights * 0.3
	logAutoLight("initial hightlights (from percentiles):", highlights)

	let clippingHighlights = 0
	if (clippingPercent > 0) {
		clippingHighlights = -Math.min(10 + clippingPercent * 100 * 10, 50)
		logAutoLight("clippingHighlights:", clippingHighlights)

		whites = clamp(whites + clippingHighlights, -35, 0)
		highlights += clippingHighlights
	}

	// Compute Shadows
	let shadows = Math.max(0.1 - shadowsMean, 0) * 150
	let blacks = clamp(shadows * 1.5, 0, 20)

	if (faceExists) {
		blacks += Math.max(0.1 - (facePercentiles[0] ?? 0), 0) * 200
		const d2 = Math.max(0.2 - (facePercentiles[1] ?? 0), 0) * 150
		blacks = clamp(blacks + d2, 0, 40)
		shadows += d2
	}

	if (faceExists) {
		whites *= 0.7 // previous whites parameter seems to affect face tone badly

		const maxDiffLow = getPercentileDifference(
			facePercentiles.concat(faceLum),
			means.slice(8),
			stds.slice(8),
			0.02,
			[4, 5, 6],
		)

		let faceShadows = -maxDiffLow * scale
		faceShadows = Math.max(faceExposure, faceShadows, imageShadows * 0.7)
		const shadowsScale = Math.min(45 + 1000 * facePercent, 55) // old: 30 - 45

		if (faceShadows > exposure) {
			let percentDelta = 1
			if (exposure > 0.1) {
				percentDelta = (faceShadows - exposure) / exposure
			} else if (faceShadows < -0.1) {
				percentDelta = -(faceShadows - exposure) / faceShadows
			}
			const delta = (faceShadows - exposure) * shadowsScale * Math.min(percentDelta, 1)
			logAutoLight({ faceShadows, exposure, percentDelta, shadows, delta })

			shadows += delta
		}
	} else if (imageShadows > exposure) {
		// non face photo shadows
		const shadowsScale = relaxLimits ? 100 : 50
		const delta = (imageShadows - exposure) * shadowsScale
		logAutoLight({ imageShadows, exposure, shadows, delta })

		shadows += delta
	}

	// Adjust exposure, highlights etc. as they affect each other
	const maxShadows = relaxLimits ? 80 : 45
	if (shadows > maxShadows && faceExists) {
		logAutoLight("initial shadows:", shadows, "added exposure:", (shadows - maxShadows) / 100)
		const expScale = faceExists ? 1 : 0
		exposure += ((shadows - maxShadows) / 100) * expScale
	}

	const limit = faceExists ? 1 : 0.95
	const maxExposureAbs99 = (limit - percentile99) * scale
	const maxExposureAbs95 = (limit - 0.05 - percentile95) * scale
	const maxExposureAbs90 = (limit - 0.1 - percentile90) * scale
	let maxExposureAbs = Math.min(maxExposureAbs99, maxExposureAbs95, maxExposureAbs90)
	if (faceExists) {
		maxExposureAbs = Math.min(maxExposureAbs, faceExposure)
	}
	if (exposure > maxExposureAbs) {
		const highlightsFromExp = Math.min((exposure - maxExposureAbs) * 100, 60)
		logAutoLight(
			"highlightsFromExp:" +
				highlightsFromExp +
				", maxExposureAbs:" +
				maxExposureAbs +
				" exposure:" +
				exposure,
		)
		highlights = Math.min(
			highlights - highlightsFromExp * 0.2,
			Math.max(-highlightsFromExp * 1.6, -70),
		)
	} else if (exposure < 0 && highlights !== 0) {
		highlights -= Math.max(exposure, -0.5) * 30
	}

	// highlights affects face area - limit the strength
	if (faceExists && faceLum > 0.55) {
		const scaleDown = Math.max(1 - (faceLum - 0.55) * 1.6, 0.4)
		logAutoLight("highlights scaleDown:", scaleDown)

		highlights = highlights * scaleDown
		whites *= scaleDown
	}
	logAutoLight("final hightlights:", highlights)

	const minHighlights = relaxLimits ? -80 : -60
	if (highlights < minHighlights && exposure > maxExposure) {
		// Mainly to avoid dark face get darker:
		const dExpScale = !faceExists ? 1 : 0
		const dExp = ((highlights - minHighlights) / 200) * dExpScale
		logAutoLight("decreased exposure for highlights:", { dExp, dExpScale })
		exposure += clamp(dExp, -0.4, 0)
	}

	// for non-face photos, decrease exposure if highlights adjustment is much lower than exposure
	if (
		!faceExists &&
		highlights < -30 &&
		exposure > highlights / 100 + 0.3 &&
		clippingPercent === 0
	) {
		const deltaHighlights = (highlights + 30) * 0.75
		exposure += deltaHighlights / 100
		highlights -= deltaHighlights
		logAutoLight("non-face exposure lowered and highlights delta:", deltaHighlights)
	}

	highlights = Math.max(minHighlights, Math.min(highlights, 0))
	shadows = Math.max(0, Math.min(shadows, maxShadows))

	// bright outdoor photo may be too dark after auto-highlights (large area of sky etc.)
	if (faceLum < 0.65 && exposure < 0 && clippingPercent > 0.008) {
		const deltaDarkArea = Math.max(
			0.7 - <number>percentiles[2],
			0.53 - <number>percentiles[1],
			0.36 - <number>percentiles[0],
			-0.25 * exposure,
		)
		const deltaBrightArea = Math.max(
			<number>percentiles[3] * 0.3 + <number>percentiles[4] * 0.7 - 0.83,
			-0.2 * exposure,
		)
		let delta = Math.min(deltaDarkArea, deltaBrightArea * 0.7 + deltaDarkArea * 0.3)
		let dScale = 4 * clamp((0.85 - <number>percentiles[0] - <number>percentiles[1]) / 0.2, 0, 1)
		dScale = Math.max(dScale, Math.min(4, (0.78 - avgLum) * 16))
		if (faceExists) {
			//delta = (facePercentiles[3]! + facePercentiles[4]!) / 2  - 0.82
			delta = 0.6 - <number>facePercentiles[1]
			dScale = 2.5
		}
		const deltaExp = (Math.max(delta, 0) * dScale * (-highlights - whites - 25)) / 70
		// const deltaExp = (-highlights - 25) / 60
		const delta1 = 0
		const delta2 = Math.min(
			Math.max((0.78 - avgLum) * 2, -exposure),
			Math.max(0.25, -1.2 * exposure),
		)
		logAutoLight({ deltaExp, deltaDarkArea, deltaBrightArea, delta1, delta2, dScale })
		exposure += clamp(deltaExp, delta1, delta2)
	}

	let minExposure = -1
	if (facePercent >= 0.02 && (faceLum < 0.4 || facePercentile99 < 0.65)) {
		minExposure = Math.max(0.4 - faceLum, 0.65 - facePercentile99) * 2
	}

	const minExposureLimit = relaxLimits ? -1.5 : -1
	const maxExposureLimit = relaxLimits ? 1.9 : 1.3
	exposure = Math.max(exposure, minExposure)
	exposure = Math.max(minExposureLimit, Math.min(exposure, maxExposureLimit))

	const saturation = getSaturationDelta(shadows, highlights, exposure, faceLum > 0)

	return {
		exposureDelta: Number(exposure.toFixed(2)),
		highlightsDelta: Math.round(highlights),
		shadowsDelta: Math.round(shadows),
		whitesDelta: Math.round(whites),
		blacksDelta: Math.round(blacks),
		contrastDelta: Math.max(0, Math.round(shadows * 0.8)),
		saturationDelta: Math.round(saturation),
	}
}

// bright face/image tends to become more saturated after large value of -highlights and shadows - tune down vibrance
export function getSaturationDelta(
	shadows: number,
	highlights: number,
	exposure: number,
	hasFace: boolean,
) {
	const deltaHS = Math.max(0, shadows - 20 - highlights - 20)
	const maxValue = hasFace ? 24 : 10
	let scale = hasFace ? 1.2 : 0.6
	if (exposure > 0.5) scale *= Math.max(0, 1.5 - exposure)
	return -Math.min((deltaHS * scale) / 6, maxValue)
}

// conversion from RGB to Luminance (max(r,g,b) + min(r,g,b)) / 2
export function getLightness(rgba: ArrayLike<number>, channel: number = 4, scale: number = 255) {
	const result: number[] = new Array(rgba.length / channel)

	for (let i = 0; i < result.length; i++) {
		const ii = i * channel // index to rgba data

		const r = <number>rgba[ii] / scale
		const g = <number>rgba[ii + 1] / scale
		const b = <number>rgba[ii + 2] / scale

		const n = Math.min(r, g, b)
		const x = Math.max(r, g, b)

		result[i] = (x + n) / 2
	}

	return result
}

// Only works for data in range of [0, 1]
export function computeHistogram(data: number[], numBins = 256): number[] {
	const bins = new Array<number>(numBins).fill(0)

	// Calculate histogram
	for (const element of data) {
		const binIndex = clamp(Math.floor(element * numBins), 0, numBins - 1)
		bins[binIndex] = (<number>bins[binIndex] ?? 0) + 1
	}

	return bins
}

export function getShadowsMean(bins: number[]) {
	const numBins = bins.length

	// Calculate cumulative sum
	const cumValues = new Array(numBins).fill(0)
	let sum = 0
	for (let i = 0; i < numBins; i++) {
		sum += <number>bins[i]
		cumValues[i] = sum
	}

	let valueSum = 0
	let average = 0
	const pIndex = 0.25 * cumValues[cumValues.length - 1]
	for (let i = 0; i < numBins; i++) {
		valueSum += (<number>bins[i] * (i + 0.5)) / numBins
		if (cumValues[i] >= pIndex) {
			average = valueSum / cumValues[i]
			break
		}
	}

	return average
}

// Only works for data in range of [0, 1]
export function computePercentiles(
	bins: number[],
	percentiles: number[] = [5, 20, 50, 75, 90, 95, 99],
): number[] {
	const numBins = bins.length // 256
	const dataMin = 0
	const dataMax = 1
	const binSize = (dataMax - dataMin) / numBins

	// Calculate cumulative sum
	const cumValues = new Array(numBins).fill(0)
	let sum = 0
	for (let i = 0; i < numBins; i++) {
		sum += bins[i] as number
		cumValues[i] = sum
	}

	// Calculate percentiles
	const results: number[] = []
	for (const percentile of percentiles) {
		const pIndex = (percentile / 100) * cumValues[cumValues.length - 1]
		let addDataMax = true
		for (let i = 0; i < cumValues.length; i++) {
			if (cumValues[i] >= pIndex) {
				results.push(dataMin + binSize * i)
				addDataMax = false
				break
			}
		}
		if (addDataMax) {
			results.push(dataMax)
		}
	}

	return results
}

export function getPercentileDifference(
	percentiles: number[],
	refs: number[],
	margins: number[],
	marginDelta = 0.02,
	excludedIndicies = [-1],
): number {
	let max_diff = -1
	let min_diff = 1
	// let sum_diff = 0
	let i = 0
	// let count = 0

	for (let j = 0; j < percentiles.length; j++) {
		const val = percentiles[j] ?? 0
		const ref = refs[j] ?? 0
		let margin = margins[j] ?? 0

		if (excludedIndicies.includes(i)) {
			i += 1
			continue
		}

		margin = Math.max(margin - marginDelta, 0)
		const diff = diffWithMargin(val, ref, margin)

		max_diff = Math.max(diff, max_diff)
		min_diff = Math.min(diff, min_diff)
		// sum_diff += diff

		i += 1
		// count += 1
	}

	let result: number

	if (max_diff * min_diff >= 0) {
		result = Math.abs(max_diff) > Math.abs(min_diff) ? max_diff : min_diff
	} else {
		result = max_diff + min_diff // ave_diff
	}

	return result
}

function diffWithMargin(val: number, ref: number, margin: number) {
	if (val > ref + margin) {
		return val - ref - margin
	} else if (val < ref - margin) {
		return val - ref + margin
	} else {
		return 0
	}
}

export function getSimpleGamma(lum: number) {
	return Math.max(0.4, 1 - Math.max(0, 0.5 - lum) * 1.5)
}

export function getAdjustedGamma(
	lumStats: LuminanceStatistics | LuminanceScoreStatistics | undefined,
	exposure: number = 0,
) {
	if (!lumStats) return 1

	const lumStatsFull: LuminanceStatistics = {
		...lumStats,
		faceCount: 0,
		facePercentiles: [],
		faceLum: 0,
		facePercent: 0,
	}
	const { exposureDelta, shadowsDelta } = getAutoExposure(lumStatsFull, true)

	const shadows = (0.2 - lumStats.shadowsMean * 0.5 - (lumStats.percentiles[0] ?? 0) * 0.5) * 2.5

	let lum = (exposureDelta * 1.8) / 3 + shadowsDelta / 100
	if (lum < 0) lum = Math.min(0, lum + 0.3)

	const adjustedLum = (Math.max(lum, shadows) + lum) / 2 - (exposure / 4) * 1.8

	const gamma = clamp(1 - adjustedLum, 0.4, 1.4)

	return Math.abs(gamma - 1) > 0.25 ? gamma : 1
}

export function getAdjustedGammaForFace(faceLumStats: FaceLuminanceStatistics | undefined) {
	if (!faceLumStats) return 1

	const avgLum = faceLumStats.faceLum

	const adjustedLum = Math.max(0.35 - avgLum, 0) * 2.5

	const gamma = clamp(1 - adjustedLum, 0.4, 1.4)

	logAiInfo("getAdjustedGammaForFace", {
		gamma,
		adjustedLum,
		avgLum,
		faceLumStats,
	})

	return Math.abs(gamma - 1) > 0.25 ? gamma : 1
}

function getLowerHalfHighlightsClipping(lum: number[]) {
	const length = lum.length
	const startIndex = Math.floor(length * 0.5)
	const endIndex = length - 1
	let sum = 0
	for (let i = startIndex; i <= endIndex; i++) {
		const lumValue = lum[i] ?? 0
		if (lumValue >= 0.995) {
			sum += 1
		}
	}
	return sum / (endIndex - startIndex + 1)
}

export function getLuminanceScoreStatistics(imageDataSmall: ImageData): LuminanceScoreStatistics {
	const smallData = imageDataSmall.data
	const lum = getLightness(smallData)
	const histogram = computeHistogram(lum)

	const avgLum = getMean(lum)

	const shadowsMean = getShadowsMean(histogram)

	// Calculate highlights clipping
	let sum = 0
	for (let i = 251; i <= 255; i++) {
		sum += histogram[i] ?? 0
	}
	const clippingPercent = sum / lum.length

	// Calculate shadows clipping
	const shadowsSum = histogram[0] ?? 0
	const shadowsClippingPercent = shadowsSum / lum.length

	const percentiles = computePercentiles(histogram)
	const lowerHalfHighlightsClippingPercent = getLowerHalfHighlightsClipping(lum)

	return {
		avgLum,
		shadowsMean,
		clippingPercent,
		shadowsClippingPercent,
		lowerHalfHighlightsClippingPercent,
		percentiles,
		lightingScore: getExposureScore(histogram, lum.length),
	}
}

export function getExposureScoreFromAuto(luminanceStats: LuminanceStatistics) {
	const autoDeltas = getAutoExposure(luminanceStats, true)
	const clippingPercent = luminanceStats.clippingPercent
	const clippingHighlights = Math.min(clippingPercent * 100 * 5, 100)

	// scoreAve: score from exposure delta: larger exposure adjustment -> lower score
	const scoreAve = (1.5 - Math.max(Math.abs(autoDeltas.exposureDelta) - 0.15, 0)) / 1.5

	// scoreHighLow: score from highlights and shadows delta: larger adjustment -> lower score; highlights has more weight
	const low = (Math.abs(autoDeltas.shadowsDelta) * 1.2 + Math.abs(autoDeltas.blacksDelta)) / 2
	let high = (Math.abs(autoDeltas.highlightsDelta) * 1.2 + Math.abs(autoDeltas.whitesDelta)) / 2
	high = Math.max(high, clippingHighlights)
	const scoreHighLow =
		((50 - Math.max(low - 10, 0)) / 100) * 0.4 + ((60 - Math.max(high - 10, 0)) / 100) * 0.6

	// faceScore: score from face, penalize overexposed face (clipScoreFace) and under exposed face (use faceLum)
	let faceScore = -1
	if (luminanceStats.faceLum > 0) {
		const faceHigh =
			(luminanceStats.facePercentiles[3] ?? 0) * 0.333 +
			(luminanceStats.facePercentiles[4] ?? 0) * 0.333 +
			(luminanceStats.facePercentiles[5] ?? 0) * 0.333
		const clipScoreFace = Math.max(0, 50 - Math.max(faceHigh - 0.7, 0) * 200) / 50
		faceScore = Math.min(clipScoreFace, 1 - Math.max(0.3 - luminanceStats.faceLum, 0) * 4)
		faceScore = Math.max(faceScore, 0)
		logAiInfo("getExposureScoreFromAuto face", { faceHigh, clipScoreFace, faceScore })
	}

	logAiInfo("getExposureScoreFromAuto", {
		autoDeltas,
		clippingHighlights,
		clippingPercent,
		luminanceStats,
	})

	// combine scores
	let finalScore = 0
	if (faceScore >= 0) {
		finalScore = Math.max(scoreAve * 1.2 + scoreHighLow * 1.8 + faceScore * 2.0, 0)
	} else {
		finalScore = Math.max(scoreAve * 2.5 + scoreHighLow * 3.5 - 0.7, 0)
	}

	const autoParams = getAutoLightParamsFromDeltas(autoDeltas)

	return { score: finalScore, autoParams }
}

export function getAutoLightParamsFromDeltas(autoDeltas: AutoExposureParams, scale = 1) {
	return {
		exposure: autoDeltas.exposureDelta * scale,
		highlights: (autoDeltas.highlightsDelta / 100) * scale,
		shadows: (autoDeltas.shadowsDelta / 100) * scale,
		whites: (autoDeltas.whitesDelta / 100) * scale,
		blacks: (autoDeltas.blacksDelta / 100) * scale,
		contrast: (autoDeltas.contrastDelta / 100) * scale,
		saturation: (autoDeltas.saturationDelta / 100) * scale,
	}
}

// This function is used to get auto lighting parameters for color match base adjustments
// It's different from the main auto lighting that runs twice and needs AI editor rendering (slow, see AI.worker.ts:calculateAutoParams)
export function getAutoLightParams(luminanceStats: LuminanceStatistics, faceHSVL: vec4 | null) {
	// Get auto lighting parameters deltas from the luminance statistics
	// main algorithm, run once with relaxLimits = true
	const autoDeltas = getAutoExposure(luminanceStats, true)

	// Use more shadows to replace exposure for images with overexposed highlights
	if (
		autoDeltas.exposureDelta > 1 &&
		autoDeltas.highlightsDelta < -50 &&
		autoDeltas.shadowsDelta < 50
	) {
		const deltaExp = clamp(autoDeltas.exposureDelta - 1, 0, 0.4)
		autoDeltas.exposureDelta -= deltaExp
		autoDeltas.shadowsDelta = clamp(autoDeltas.shadowsDelta + deltaExp * 80, 0, 95)
		autoDeltas.contrastDelta = clamp(autoDeltas.contrastDelta + deltaExp * 40, 0, 70)
	}

	// lower scale for true dark-skin faces to avoid making them too white/bright
	const scale =
		!isFaceProblematic(faceHSVL) && isFaceDarkSkin(faceHSVL) && autoDeltas.exposureDelta > 0.0
			? 0.5
			: 0.8
	return getAutoLightParamsFromDeltas(autoDeltas, scale)
}

export function getExposureScore(histogram: number[], totalPixels: number): number {
	let sum = 0
	let p1 = -1,
		p5 = -1,
		p15 = -1,
		p85 = -1,
		p95 = -1,
		p99 = -1

	// Get percentiles
	for (let i = 0; i < histogram.length; i++) {
		sum += histogram[i] ?? 0
		if (p1 === -1 && sum >= 0.01 * totalPixels) {
			p1 = i
		}
		if (p5 === -1 && sum >= 0.05 * totalPixels) {
			p5 = i
		}
		if (p15 === -1 && sum >= 0.15 * totalPixels) {
			p15 = i
		}
		if (p85 === -1 && sum >= 0.85 * totalPixels) {
			p85 = i
		}
		if (p95 === -1 && sum >= 0.95 * totalPixels) {
			p95 = i
		}
		if (p99 === -1 && sum >= 0.99 * totalPixels) {
			p99 = i
		}
	}

	// Penalize under or over exposure in small region
	const numOfBuckets = histogram.length
	const u = 0.08
	const o = 0.08
	let cu = Math.min(u, (p15 - p1) / numOfBuckets) / u // under-exposure factor
	let co = Math.min(o, (p99 - p85) / numOfBuckets) / o // over-exposure factor
	if (p95 < 225) co = Math.max(0.8, co)
	if (cu < 0.8 && p5 > 20) cu = Math.min(0.8, cu + (p5 - 20) / 20.0) // not completely dark

	co = Math.max(0.1, co)
	cu = Math.max(0.1, cu)

	let delta = Math.abs(p95 - p5) / numOfBuckets
	delta = 0.7 * delta + 0.3 * (Math.abs(p99 - p1) / numOfBuckets)

	const tone = cu * co * delta // adjusted dynamic range

	let averageLuminance = 0.5 // average lighting excluding very bright/dark pixels

	// Only consider average luminance when dynamic range is large
	if (tone > 0.4 && co > 0.95) {
		let totalLuminance = 0
		let lumiCount = 0
		for (let i = 25; i < 230; i++) {
			totalLuminance += i * (histogram[i] ?? 0)
			lumiCount += histogram[i] ?? 0
		}
		averageLuminance = totalLuminance / lumiCount / 255.0
	}

	const score = tone * 6.5 + (Math.min(Math.max(averageLuminance, 0.4), 0.6) - 0.5) * 10 // 0 - 7.5

	return score
}

export function adjustExposureScoreWithFace(score: number, faceExposure: number) {
	// Adjust score based on face exposure
	if (faceExposure >= 0 && faceExposure < 0.35) {
		score *= Math.max(0, (faceExposure - 0.05) / 0.3)
	} else if (faceExposure > 0.45 && faceExposure <= 0.64 && score <= 3.0) {
		score += (3.5 - score) * faceExposure
	} else if (faceExposure > 0.64 && faceExposure < 0.8) {
		score *= (1 - faceExposure) * 2.8
	} else if (faceExposure >= 0.8) {
		score *= 1 - faceExposure
	}

	if (faceExposure > 0.25 && faceExposure < 0.5) {
		score = Math.max(score, clamp((faceExposure - 0.18) * 15, 1, 3.5))
	}

	return score
}

export function getNormalizedExposureScore(score: number) {
	const standard: [number, number, number, number, number] = [1.3, 2.0, 3.1, 3.6, 4.7]

	if (score <= standard[0]) {
		score /= standard[0]
	} else if (score <= standard[1]) {
		score = (score - standard[0]) / (standard[1] - standard[0]) + 1.0
	} else if (score <= standard[2]) {
		score = (score - standard[1]) / (standard[2] - standard[1]) + 2.0
	} else if (score <= standard[3]) {
		score = (score - standard[2]) / (standard[3] - standard[2]) + 3.0
	} else {
		score = (score - standard[3]) / (standard[4] - standard[3]) + 4.0
	}

	return score
}
